import type { TenantRole } from '@servicetim/shared';
import { linkOptions } from '@tanstack/react-router';
import {
  Bell,
  Building2,
  ClipboardCheck,
  FileInput,
  FolderKanban,
  Kanban,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  ListChecks,
  ListTodo,
  type LucideIcon,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  UsersRound,
  Webhook,
  Zap,
} from 'lucide-react';

export const APP_NAME = 'ServiceTim';
export const SURFACE_LABEL = 'Admin';

/**
 * Sidebar sections. `linkOptions` type-checks every `to` against the registered router, so a
 * renamed route fails `tsc` here instead of 404-ing at runtime. `label`/`icon`/`minRole` ride
 * along untouched. `minRole` hides the entry from lower tenant roles (the route itself shows a
 * not-allowed state; the server enforces the rule).
 */
export const NAV_SECTIONS = [
  {
    title: 'Work',
    items: linkOptions([
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, activeOptions: { exact: true } },
      { to: '/queue', label: 'Queue', icon: ListTodo },
      { to: '/board', label: 'Board', icon: Kanban },
      { to: '/jobs', label: 'Jobs', icon: ListChecks },
      { to: '/approvals', label: 'Approvals', icon: ClipboardCheck },
      { to: '/notifications', label: 'Notifications', icon: Bell },
    ]),
  },
  {
    title: 'Configure',
    items: linkOptions([
      { to: '/projects', label: 'Projects', icon: FolderKanban },
      { to: '/templates', label: 'Templates', icon: LayoutTemplate },
      { to: '/permission-schemes', label: 'Permission schemes', icon: ShieldCheck },
      { to: '/imports', label: 'Imports', icon: FileInput },
    ]),
  },
  {
    title: 'Tenant',
    minRole: 'admin' as TenantRole,
    items: linkOptions([
      { to: '/tenant', label: 'Tenant settings', icon: Settings },
      { to: '/users', label: 'Users', icon: Users },
      { to: '/groups', label: 'Groups & teams', icon: UsersRound },
      { to: '/organizations', label: 'Customer organizations', icon: Building2 },
      { to: '/fields', label: 'Tenant fields', icon: SlidersHorizontal },
      { to: '/roles', label: 'Role templates', icon: UserCog },
      { to: '/api-keys', label: 'API keys', icon: KeyRound },
      { to: '/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/link-types', label: 'Link types', icon: Link2 },
      { to: '/automations', label: 'Automations', icon: Zap },
      { to: '/audit', label: 'Audit log', icon: ScrollText },
    ]),
  },
];

export type NavSection = (typeof NAV_SECTIONS)[number];
export type NavItem = NavSection['items'][number];
export type NavIcon = LucideIcon;

/** Every entry, flattened (kept for callers of the Bolt 1 shape). */
export const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap(
  (section): readonly NavItem[] => section.items,
);
